import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Network,
  Users,
  MessageSquare,
  Settings as SettingsIcon,
  LogOut,
  BarChart2,
  GitBranch,
  Activity,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testId: "nav-dashboard" },
  { to: "/organogram", label: "Organogram", icon: Network, testId: "nav-organogram" },
  { to: "/analytics", label: "Analytics", icon: BarChart2, testId: "nav-analytics" },
  { to: "/scenarios", label: "Scenarios", icon: GitBranch, testId: "nav-scenarios" },
  { to: "/directory", label: "Directory", icon: Users, testId: "nav-directory" },
  { to: "/audit", label: "Audit log", icon: Activity, testId: "nav-audit" },
  { to: "/teams", label: "Teams", icon: MessageSquare, testId: "nav-teams" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testId: "nav-settings" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen bg-[#F4F4F2] text-neutral-900" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
      <aside
        className="sticky top-0 flex h-screen w-64 shrink-0 flex-col border-r border-neutral-200 bg-white"
        data-testid="app-sidebar"
      >
        <div className="flex h-16 items-center border-b border-neutral-200 px-4">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            <div className="h-6 w-6 bg-[#002FA7]" />
            <span className="text-sm font-semibold tracking-tight" style={{ fontFamily: '"Work Sans", sans-serif' }}>
              ORGANOGRAM
            </span>
          </Link>
        </div>

        <nav className="flex-1 px-2 py-4">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testId}
                className={({ isActive }) =>
                  `mb-1 flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                    isActive
                      ? "bg-neutral-100 text-black"
                      : "text-neutral-600 hover:bg-neutral-50 hover:text-black"
                  }`
                }
              >
                <Icon size={16} className="shrink-0" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-neutral-200 p-3">
          <div className="flex items-center gap-3">
            <Avatar className="h-8 w-8 rounded-none">
              <AvatarImage src={user?.photo_url || ""} />
              <AvatarFallback className="rounded-none bg-[#002FA7] text-xs text-white">
                {(user?.name || "U").slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{user?.name}</p>
              <p className="truncate text-[10px] uppercase tracking-wider text-neutral-500">
                {user?.role}
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="text-neutral-500 hover:text-[#C50F1F]"
              data-testid="logout-button"
              aria-label="Logout"
            >
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">{children}</main>
    </div>
  );
}
